import { useEffect, useRef, useState, type ReactNode } from 'react';
import { FareExpansion, type TariffFetchState } from '../../fares/fare_expansion.js';
import { defaultSel, type FareSel } from '../../fares/fare_selection.js';
import type { FareTextClassifier, FlightTariffSource } from '../../fares/fare_source.js';
import { OfferFaresDialog } from '../../fares/fares_dialog.js';
import type { FaresRequestText, FlightAirportSwitchFn } from '../../fares/fares_request_page.js';
import { carrierNameOf, type FlightOfferSource } from '../flight_offer_source.js';
import { useDetailLayer, type RenderFlightDetail } from './detail_layer.js';
import { FlightPolicyDrawer, type FlightPolicyCopy } from './flight_policy_drawer.js';
import { CAROUSEL_RAIL, CarouselDots } from './offer_carousel.js';
import type { FlightAirportSwitchView } from './offer_copy.js';
import { FlightOfferRow } from './offer_row.js';
import { useOfferLayout, type OfferLayout } from './use_offer_layout.js';
import { usePhoneViewport } from './use_phone_viewport.js';
import { useStableCallback } from '../../../shared/use_stable_callback.js';
import './flight_offers.css';

/** A captura do tariff de uma oferta e o reconhecimento do handle morto (409
 *  `offer_stale`, #2332) e da perna sem tarifa (409 `leg_unavailable`, #6000),
 *  que ganham fase própria na escada. */
export type FlightTariffPort<V extends FlightTariffSource = FlightTariffSource> = {
  readonly get: (offerId: string) => Promise<V>;
  readonly isStale: (error: unknown) => boolean;
  /** A perna que a companhia tarifou sem nenhuma tarifa; `undefined` se o erro é outro. */
  readonly unavailableLeg: (error: unknown) => 'outbound' | 'return' | undefined;
};

/** O estado da captura que falhou. Handle morto (409 offer_stale, #2332) e perna
 *  sem tarifa (#6000) ganham fase própria: o retry do estado `error` repetiria o
 *  mesmo 409 para sempre. */
function failedState(
  port: Pick<FlightTariffPort, 'isStale' | 'unavailableLeg'>,
  err: unknown,
): Exclude<TariffFetchState, { phase: 'loading' | 'ready' }> {
  const leg = port.unavailableLeg(err);
  if (leg) return { phase: 'leg_unavailable', leg };
  return { phase: port.isStale(err) ? 'stale' : 'error' };
}

/** O que a lista lê do host além das ofertas: os textos da linha, da escada e
 *  da tela de tarifas, a gaveta de política, o desvio de aeroporto e o drawer de
 *  detalhe (#4997). */
export type OfferListEnv = {
  readonly text: FaresRequestText;
  readonly policy: FlightPolicyCopy;
  readonly airportSwitchOf: FlightAirportSwitchFn;
  readonly renderDetail: RenderFlightDetail;
  /** O drawer de detalhe abriu (fechado → aberto) — a telemetria do host. */
  readonly onDetailOpened?: () => void;
};

/**
 * O estado da escada de tarifas de uma LISTA de ofertas: qual está expandida, o
 * fetch cacheado por oferta e a seleção corrente. Vive aqui (e não em cada card)
 * porque o `flight_offers` e o `flight_multi_offers` renderizam a MESMA escada — a
 * peça que mais mudou de forma nos últimos meses não pode ter duas
 * implementações.
 *
 * O cache é por `offerId` e nunca é limpo: no multi-trecho os ids são únicos entre
 * trechos, então voltar num trecho já visitado não paga o fetch de novo.
 */
export interface OfferFares<
  O extends FlightOfferSource = FlightOfferSource,
  V extends FlightTariffSource = FlightTariffSource,
> {
  /** Port + canal presentes: a linha vira toggle e a escada abre. */
  enabled: boolean;
  expandedId: string | null;
  toggle: (offer: O) => void;
  /** Fecha a escada aberta, sem clique na linha. Quem usa é o host cuja PERNA
   *  fecha no pick (o explorer): a escada aberta sobre um trecho já resolvido
   *  contradizia o passo — o placar dizia "2 de 2" com o painel de escolha ainda
   *  na tela. O card multi NÃO usa: lá a escada do trecho resolvido reabre de
   *  propósito (`autoExpandId`), porque é ela que prova qual oferta venceu. */
  collapse: () => void;
  pick: (offerId: string, patch: Partial<FareSel>) => void;
  fetch: (offer: O) => void;
  /**
   * O caminho ÚNICO de captura de uma oferta — usado pelo fetch fire-and-forget
   * da própria lista (toggle/autoExpand/retry) E por quem precisa AGUARDAR a
   * view, como o lote das tarifas mais baratas do card multi-trecho. Uma oferta já
   * `ready` resolve na hora (sem pedir `/flight/tariff` de novo); uma captura
   * JÁ em voo (toggle manual concorrente com o lote, ou vice-versa) devolve a
   * MESMA promise — dois chamadores da mesma oferta nunca disparam duas
   * chamadas. É o que fecha a invariante "uma oferta, uma captura por vida do
   * card": antes desta função o lote pedia `getTariff` por fora da escada, e
   * reabrir a escada no trecho que o lote fechou refazia a MESMA captura — a
   * segunda substituía as pricing options da primeira no modal-session, e o
   * `fareId` que o pick guardou morria (QA dev 09/14, conversa E2).
   */
  capture: (offer: O) => Promise<V>;
  /** Leitura da escada de UMA oferta — quem consome é o `OfferListBody`, e só ele.
   *  O card recebe `state` e `sel` prontos no `onSelectFares`, então nunca precisa
   *  dos mapas: expor os `Record`s era o que fazia o card re-derivar a seleção na
   *  hora de despachar. Nunca-buscada = `loading` (a linha abre com shimmer) e
   *  seleção vazia. */
  stateOf: (offerId: string) => TariffFetchState<V>;
  selOf: (offerId: string) => FareSel;
}

/**
 * A seleção que corresponde a um `fareId` JÁ decidido, resolvida contra o tariff
 * que acabou de chegar — a inversa do `fareIdOf` do card multi-trecho. `null`
 * quando o id não está mais na escada (tarifa expirada, que neste fluxo é
 * transitória e acontece de verdade), e aí a semente cede pro `defaultSel`: abrir
 * a escada SEM seleção travaria o CTA da tarifa.
 *
 * UM id resolve a seleção inteira porque quem semeia é o multi-trecho, e lá cada
 * trecho é one-way. Um round-trip split precisaria de dois ids e não tem produtor.
 */
function selFromFareId(view: FlightTariffSource, fareId: string): FareSel | null {
  if (view.bundled.some((c) => c.fareId === fareId)) return { bundled: fareId };
  if (view.departing.some((f) => f.fareId === fareId)) return { outbound: fareId };
  return null;
}

/**
 * `autoExpandId` abre uma oferta e dispara o fetch dela — na montagem (card
 * focado do `flight_offers`) e a cada TROCA do valor (o multi-trecho reabre a
 * escada do card que venceu o trecho ao voltar nele). `null` = nada aberto.
 *
 * `seedFare` é a escolha JÁ tomada de UMA oferta (no multi-trecho: o eco do
 * servidor). Ela substitui o `defaultSel` quando o tariff dela chega — sem isso a
 * escada de um trecho já decidido reabre na MAIS BARATA (o default), e um "Selecionar" sem
 * tocar em nada troca a tarifa (e o preço) que a pessoa já havia escolhido.
 */
export function useOfferFares<O extends FlightOfferSource, V extends FlightTariffSource>(
  offers: readonly O[],
  {
    enabled,
    autoExpandId = null,
    seedFare = null,
    tariff: port,
    classify,
  }: {
    enabled: boolean;
    autoExpandId?: string | null;
    seedFare?: { offerId: string; fareId: string } | null;
    /** Sem porta, a captura rejeita: o host não liga `enabled` sem ela. */
    tariff: FlightTariffPort<V> | undefined;
    /** O classificador do texto do fornecedor: a seleção padrão da escada. */
    classify: FareTextClassifier;
  },
): OfferFares<O, V> {
  const [expandedId, setExpandedId] = useState<string | null>(enabled ? autoExpandId : null);
  const [fetchStates, setFetchStates] = useState<Record<string, TariffFetchState<V>>>({});
  const [sels, setSels] = useState<Record<string, FareSel>>({});
  // Capturas em VOO, por oferta — o que faz dois chamadores da MESMA oferta (o
  // toggle manual e o lote das tarifas mais baratas, ou os dois lotes) compartilhar a
  // MESMA promise em vez de pedir `/flight/tariff` duas vezes. Ref e não estado:
  // dedupe não é UI, e um `setState` aqui só forçaria um re-render à toa.
  const inFlight = useRef(new Map<string, Promise<V>>());

  /**
   * O caminho ÚNICO de captura (ver `OfferFares.capture`). Oferta `ready`
   * resolve na hora; captura já em voo devolve a MESMA promise; senão busca,
   * grava o resultado e libera o slot em voo — sucesso OU falha — pra uma
   * captura seguinte poder tentar de novo (um estado `error`/`stale` não trava
   * a invariante: a PRÓXIMA captura desta oferta é livre pra pedir de novo).
   *
   * Recebe a OFERTA e não só o id porque é a oferta que o `toggle`/`autoExpandId`
   * já têm em mão; o default de seleção sai do TARIFF (ver `hasReturnLadder` em
   * fare_expansion), nunca de um campo da oferta.
   */
  const capture = (offer: O): Promise<V> => {
    const offerId = offer.offerId;
    const known = fetchStates[offerId];
    if (known?.phase === 'ready') return Promise.resolve(known.view);
    const flying = inFlight.current.get(offerId);
    if (flying) return flying;
    if (!port) return Promise.reject(new Error('offer_list: sem port de tarifas'));
    setFetchStates((s) => ({ ...s, [offerId]: { phase: 'loading' } }));
    const promise = port.get(offerId).then(
      (view) => {
        inFlight.current.delete(offerId);
        setFetchStates((s) => ({ ...s, [offerId]: { phase: 'ready', view } }));
        // A semente é lida AQUI, no `.then`, porque é aqui que o default aterrissa
        // — e o fechamento é o do render que disparou a captura, o mesmo que
        // decidiu abrir esta escada, então ela nunca perde a corrida do await.
        const seeded = seedFare?.offerId === offerId ? selFromFareId(view, seedFare.fareId) : null;
        // Só quando AUSENTE, como sempre: a semente substitui o default, nunca uma
        // seleção que a pessoa já fez à mão.
        setSels((s) =>
          s[offerId] ? s : { ...s, [offerId]: seeded ?? defaultSel(view, classify) },
        );
        return view;
      },
      (err: unknown) => {
        inFlight.current.delete(offerId);
        setFetchStates((s) => ({ ...s, [offerId]: failedState(port, err) }));
        throw err;
      },
    );
    inFlight.current.set(offerId, promise);
    return promise;
  };

  // Fetch fire-and-forget (toggle/autoExpand/retry): a UI não espera a
  // promise, o estado (`loading`/`ready`/`error`/`stale`) já é o que ela lê. O
  // `.catch` aqui só evita a rejeição não tratada — o estado de erro já foi
  // gravado dentro de `capture`.
  const fetchTariff = (offer: O) => {
    void capture(offer).catch(() => {});
  };

  // Roda na montagem e a cada troca de `autoExpandId`. O estado inicial já vale o
  // mesmo valor, então na montagem não há troca visual — o efeito só existe pelo
  // fetch. `offers`/`fetchStates` são lidos os de agora, sem armar o efeito:
  // reagir a eles re-dispararia o fetch.
  const autoExpand = useStableCallback((id: string | null) => {
    setExpandedId(id);
    const offer = offers.find((o) => o.offerId === id);
    if (offer && !fetchStates[offer.offerId]) fetchTariff(offer);
  });
  useEffect(() => {
    if (enabled) autoExpand(autoExpandId);
  }, [autoExpandId, enabled, autoExpand]);

  return {
    enabled,
    expandedId,
    // `expandedId`/`fetchStates` lidos do fechamento DESTE render de propósito: é o
    // comportamento de sempre (o clique que fecha a escada não dispara fetch), e
    // trocar por atualização funcional mudaria quando o fetch sai.
    toggle: (offer) => {
      const id = offer.offerId;
      setExpandedId((cur) => (cur === id ? null : id));
      if (expandedId !== id && !fetchStates[id]) fetchTariff(offer);
    },
    collapse: () => {
      setExpandedId(null);
    },
    pick: (offerId, patch) => {
      setSels((s) => ({ ...s, [offerId]: { ...s[offerId], ...patch } }));
    },
    fetch: fetchTariff,
    capture,
    stateOf: (offerId) => fetchStates[offerId] ?? { phase: 'loading' },
    selOf: (offerId) => sels[offerId] ?? {},
  };
}

/**
 * A capacidade de FIXAR uma oferta (#2294 — "pinar para comprar depois"),
 * injetada pelo host (a bancada) e repassada crua à linha. O kit não guarda o
 * estado: `isPinned` lê e `toggle` escreve no store do host (`state/flight_pins`
 * no web-chat), fixando pelo `offerId` — o handle opaco que já identifica a
 * oferta em todo lugar. Ausente (o padrão) ⇒ nenhuma linha ganha o toggle: os
 * cards do chat (`flight_offers`/`flight_multi_offers`) não fixam, só o explorer
 * da bancada arma isto.
 */
export interface FlightPinControl<O extends FlightOfferSource = FlightOfferSource> {
  isPinned: (offerId: string) => boolean;
  toggle: (offer: O) => void;
}

/** O que a linha faz quando a tarifa fecha. Mesma assinatura nos três hosts. */
export type SelectFaresFn<
  O extends FlightOfferSource = FlightOfferSource,
  V extends FlightTariffSource = FlightTariffSource,
> = (
  offer: O,
  position: number,
  state: TariffFetchState<V>,
  picked: { total: number; currency: string; names: string },
  sel: FareSel,
) => void;

/** As props de AÇÃO da linha, por modo — escada (toggle + expansão inline, ou
 *  a tela cheia do celular), seleção (a linha escolhe a oferta) ou
 *  display-only. Fora do componente porque é a única ramificação da lista:
 *  assim o `OfferListBody` fica com a casca (as duas superfícies de leitura) e
 *  a linha nunca ganha uma segunda montagem em outro host. */
function rowActions<O extends FlightOfferSource, V extends FlightTariffSource>({
  offer,
  position,
  fares,
  faresDialog,
  readOnly,
  soloLegLabel,
  fareCta,
  onSelectFares,
  onSelectOffer,
  onResearch,
  researching,
  text,
}: {
  offer: O;
  position: number;
  fares: OfferFares<O, V>;
  /** Presente no card EMPILHADO (#3875): o rodapé abre a tela cheia em vez da
   *  expansão inline, e `openId` é a oferta que ela mostra. */
  faresDialog?: { openId: string | null; open: (offer: O) => void };
  readOnly?: boolean;
  soloLegLabel?: string;
  fareCta?: { mode: 'confirm'; disabled?: boolean };
  onSelectFares: SelectFaresFn<O, V>;
  onSelectOffer?: (offer: O) => void;
  onResearch?: () => void;
  researching?: boolean;
  text: FaresRequestText;
}): {
  onToggleFares?: (offer: O) => void;
  expanded?: boolean;
  expansion?: ReactNode;
} {
  // Sem escada (`fares.enabled` desligado — port ausente, ou a fase 1 do RT,
  // onde tarifa é assunto da fase 2): a linha SELECIONA, ou fica só de leitura.
  // Sem `expanded`, o CTA do trilho vira select-mode (aria
  // `flightFilter.selectOfferAria`) — a mesma linha, outro verbo.
  if (!fares.enabled) {
    return onSelectOffer && !readOnly ? { onToggleFares: onSelectOffer } : {};
  }
  // No celular a escada não abre embaixo do card: o rodapé é a porta da tela
  // cheia, e o `expandedId` do hook (o autoExpand do card focado, a perna que
  // o roteiro reabre) não a abre sozinho — uma tela inteira por cima da
  // conversa só entra por um toque.
  if (faresDialog) {
    return { onToggleFares: faresDialog.open, expanded: faresDialog.openId === offer.offerId };
  }
  const expanded = fares.expandedId === offer.offerId;
  if (!expanded) return { onToggleFares: fares.toggle, expanded };
  return {
    onToggleFares: fares.toggle,
    expanded,
    expansion: (
      <FareExpansion
        state={fares.stateOf(offer.offerId)}
        sel={fares.selOf(offer.offerId)}
        readOnly={readOnly}
        {...(soloLegLabel ? { soloLegLabel } : {})}
        legCarriers={[
          carrierNameOfFirst(offer.segments) || undefined,
          offer.returnLeg ? carrierNameOfFirst(offer.returnLeg.segments) || undefined : undefined,
        ]}
        {...(fareCta ? { cta: fareCta } : {})}
        onPick={(patch) => {
          fares.pick(offer.offerId, patch);
        }}
        onSelect={(picked) => {
          onSelectFares(
            offer,
            position,
            fares.stateOf(offer.offerId),
            picked,
            fares.selOf(offer.offerId),
          );
        }}
        onRetry={() => {
          fares.fetch(offer);
        }}
        {...(onResearch ? { onResearch } : {})}
        researching={researching}
        text={text}
      />
    ),
  };
}

/** A companhia do primeiro trecho de uma perna, vazia sem trecho. */
function carrierNameOfFirst(segments: FlightOfferSource['segments']): string {
  const first = segments[0];
  return first ? carrierNameOf(first) : '';
}

export interface OfferListBodyProps<
  O extends FlightOfferSource,
  V extends FlightTariffSource = FlightTariffSource,
> {
  readonly offers: readonly O[];
  readonly fares: OfferFares<O, V>;
  readonly env: OfferListEnv;
  readonly readOnly?: boolean;
  /** No celular a lista vira CARROSSEL (#3874): cards de 320px lado a lado, com
   *  pontos. Só os cards da conversa pedem — `flight_offers` e o trecho ativo
   *  do `flight_multi_offers` (#4879); a gaveta "ver todos" e o explorer seguem
   *  empilhados (o card é o mesmo). Em container largo não muda nada. */
  readonly carousel?: boolean;
  /** A superfície do card do celular — ver `FlightOfferRowProps.variant`. O
   *  card do chat e a gaveta passam `chat`; o explorer, `results` (#3893: cards
   *  a 16px, não a 24px); o roteiro fica no padrão. */
  readonly variant?: 'chat' | 'itinerary' | 'results';
  /** Nome da perna quando o painel de tarifas tem escada ÚNICA. O roteiro
   *  multi-trecho manda "Trecho N"; ausente ⇒ o painel diz "Ida", que é o certo
   *  no card binário. */
  readonly soloLegLabel?: string;
  /** Modo Confirmar do rodapé da escada — repassado cru ao `FareExpansion` de
   *  CADA linha (`rowActions`). Só o EXPLORER manda (o pick da vez é o último
   *  trecho pendente); os cards do chat (`flight_offers`/`flight_multi_offers`)
   *  nunca passam, e o rodapé segue "Selecionar". */
  readonly fareCta?: { mode: 'confirm'; disabled?: boolean };
  /** Rótulo do toggle da escada nas linhas — ver `RailCta`. Ausente ⇒
   *  "Ver tarifas" (card do chat); o explorer passa `Selecionar`. */
  readonly faresCtaLabel?: string;
  /** Nome acessível da lista. Ausente ⇒ `<ul>` sem rótulo (o card já tem título
   *  visível); a fase 1 do round-trip no explorer o usa porque lá o rótulo é a
   *  pergunta da fase ("Escolha o voo de ida"). */
  readonly listLabel?: string;
  /** `offerId` marcado como escolhido (eco do servidor). Repasse cru: a lista não
   *  decide nada, só sabe QUAL linha leva o tique. */
  readonly chosenOfferId?: string;
  /** O preço das linhas é o do PAR ida+volta (fases do RT no explorer: célula da
   *  matriz por perna, sem `returnLeg` na oferta). Só muda a legenda do preço. */
  readonly pairPricing?: boolean;
  /** Fixar/desfixar por linha (#2294). Ausente ⇒ sem toggle (cards do chat). Só
   *  a bancada passa — e mesmo lá some no `readOnly` (checkout), como o resto dos
   *  gestos da linha. */
  readonly pin?: FlightPinControl<O>;
  /** A troca de aeroporto da volta por linha (#4203) — só a fase 2 do ida e
   *  volta da bancada passa (`rowPropsOf`). */
  readonly airportSwitchOf?: (offer: O) => FlightAirportSwitchView | null;
  /** `picked` é o resumo que o painel mostrou (total/moeda/nomes); `sel` são os ids
   *  opacos da seleção corrente. Os dois chegam prontos pra quem despacha — é o que
   *  mantém o estado da escada fechado dentro do `useOfferFares`. */
  readonly onSelectFares: SelectFaresFn<O, V>;
  /** Lista em modo SELEÇÃO: sem escada, a linha inteira (e o CTA do trilho)
   *  escolhem a oferta. É a fase 1 do round-trip do explorer — lá se escolhe o
   *  VOO; as tarifas reais são as da matriz do par, que só existe na fase 2. Só
   *  vale com a escada desabilitada e fora de leitura; assim a MESMA lista serve
   *  as duas fases sem o host montar uma `<ul>` própria. */
  readonly onSelectOffer?: (offer: O) => void;
  /** Recuperação do estado `stale` da escada (handle morto, #2332): o host que
   *  TEM como re-buscar passa aqui o gesto (abrir o explorer, re-rodar a busca
   *  da fase). Ausente ⇒ a faixa stale fica sem CTA — melhor que um retry que
   *  repete o mesmo 409. */
  readonly onResearch?: () => void;
  /** A re-busca do `onResearch` está em voo (#5277): a faixa stale trava o CTA e
   *  diz que a busca está andando. Só os cards do chat passam — a deles é um
   *  turno da conversa, que leva o tempo de uma busca. */
  readonly researching?: boolean;
}

/**
 * A lista de ofertas com a escada (inline no desenho largo; em tela cheia no
 * card do celular, #3875) e os dois drawers de leitura (política e detalhe do
 * voo). Os drawers são estado interno da lista — nenhum shell
 * intercepta uma ação pra abri-los, e onde eles PINTAM não depende de onde o card
 * os renderiza: o de política é o `SidePanel` do DS (portal, acima de tudo) e o de
 * detalhe vai num portal pro `body`.
 *
 * Quem chama é dono de UMA coisa: o que acontece quando a tarifa é escolhida
 * (`onSelectFares`). No `flight_offers` isso despacha o pick; no
 * `flight_multi_offers` isso grava a escolha do trecho localmente; no explorer o
 * pick vira o `fares_picked` da perna corrente.
 */
export function OfferListBody<O extends FlightOfferSource, V extends FlightTariffSource>({
  offers,
  fares,
  env,
  readOnly,
  listLabel,
  soloLegLabel,
  fareCta,
  faresCtaLabel,
  chosenOfferId,
  pairPricing,
  pin,
  carousel,
  variant,
  airportSwitchOf,
  onSelectFares,
  onSelectOffer,
  onResearch,
  researching,
}: OfferListBodyProps<O, V>) {
  const { text } = env;
  const { ref: railRef, layout } = useOfferLayout<HTMLUListElement>();
  const asCarousel = Boolean(carousel) && layout === 'stack';
  const [policyOffer, setPolicyOffer] = useState<O | null>(null);
  // A tela cheia exige o card EMPILHADO e a VIEWPORT de celular: só o container
  // estreito (a coluna do explorer numa story, um painel) desenha o card do
  // celular, mas cobrir a janela inteira de um desktop por causa de uma coluna
  // seria errar o gesto — ali a escada segue inline.
  const phoneViewport = usePhoneViewport();
  const faresDialog = useListFaresDialog(offers, fares, layout === 'stack' && phoneViewport);
  // Na tela em diálogo do explorer o detalhe monta dentro dela.
  const detailLayer = useDetailLayer(env.renderDetail);
  const detail = detailLayer.detail;
  useCollapseOnStack(layout, fares);
  // Telemetria por TRANSIÇÃO (fechado → aberto), não por clique: o drawer é
  // push/não-modal e a lista segue interativa com ele aberto — comparar a
  // oferta A e depois clicar "Detalhes" da B troca o CONTEÚDO do mesmo
  // drawer já aberto, não reabre um novo (mesma régua do detalhe da reserva,
  // ver `flight_reservation.tsx`).
  const wasDetailOpen = useRef(false);
  useEffect(() => {
    const isOpen = detail !== null;
    if (isOpen && !wasDetailOpen.current) env.onDetailOpened?.();
    wasDetailOpen.current = isOpen;
  }, [detail, env]);

  // "Detalhes" abre o drawer mesmo em leitura (view-only — o rodapé "Selecionar
  // voo" saiu com o caminho legado de cotação). É a única porta da lista: o
  // link "Assentos" (#2363), que abria este mesmo drawer com `focus: 'seats'`,
  // saiu em #2514 — o mapa segue dentro do drawer.
  function openDetail(offer: O) {
    const flight = offer.segments[0];
    detailLayer.open({
      offerId: offer.offerId,
      summary: {
        ...(flight?.carrier ? { carrier: flight.carrier } : {}),
        ...(flight?.carrierName ? { carrierName: flight.carrierName } : {}),
        ...(flight?.flightNumber ? { flightNumber: flight.flightNumber } : {}),
        from: flight?.from ?? '',
        to: flight?.to ?? '',
      },
    });
  }

  return (
    <>
      <ul
        ref={railRef}
        className={`fq-list fq-list--flight @container ${listClass(asCarousel, variant, layout)}`}
        {...(listLabel ? { 'aria-label': listLabel } : {})}
      >
        {offers.map((o, i) => (
          <FlightOfferRow
            key={o.offerId}
            layout={layout}
            slide={asCarousel}
            {...(variant ? { variant } : {})}
            offer={o}
            position={i + 1}
            pairPricing={pairPricing}
            onOpenPolicy={setPolicyOffer}
            faresCtaLabel={faresCtaLabel}
            copy={text.copy.offer}
            locale={text.locale}
            places={text.places}
            // Lambda e não a referência nua: a linha chama `onDetail(offer,
            // position)`, e o `openDetail` não quer o `position`.
            onDetail={(o) => {
              openDetail(o);
            }}
            chosen={o.offerId === chosenOfferId}
            airportSwitch={airportSwitchOf?.(o)}
            {...(pin
              ? {
                  pin: {
                    pinned: pin.isPinned(o.offerId),
                    onToggle: () => {
                      pin.toggle(o);
                    },
                  },
                }
              : {})}
            {...rowActions({
              offer: o,
              position: i + 1,
              fares,
              ...(faresDialog.control ? { faresDialog: faresDialog.control } : {}),
              readOnly,
              ...(soloLegLabel ? { soloLegLabel } : {}),
              ...(fareCta ? { fareCta } : {}),
              onSelectFares,
              ...(onSelectOffer ? { onSelectOffer } : {}),
              ...(onResearch ? { onResearch } : {}),
              researching,
              text,
            })}
          />
        ))}
      </ul>
      {asCarousel && (
        <CarouselDots railRef={railRef} count={offers.length} slideLabel={text.copy.offer.slide} />
      )}
      {faresDialog.offer && (
        <ListFaresDialog
          offer={faresDialog.offer}
          position={faresDialog.position}
          fares={fares}
          readOnly={readOnly}
          fareCta={fareCta}
          env={env}
          onSelectFares={onSelectFares}
          onResearch={onResearch}
          onClose={faresDialog.close}
        />
      )}
      {policyOffer && (
        <FlightPolicyDrawer
          offer={policyOffer}
          copy={env.policy}
          locale={text.locale}
          onClose={() => {
            setPolicyOffer(null);
          }}
        />
      )}
      {detailLayer.node}
    </>
  );
}

/** As classes do `<ul>`: carrossel no chat; senão a grade — a 24px na linha
 *  desktop e no roteiro, a 16px nos cards do resultado determinístico (#3893). */
function listClass(
  asCarousel: boolean,
  variant: 'chat' | 'itinerary' | 'results' | undefined,
  layout: OfferLayout,
): string {
  if (asCarousel) return CAROUSEL_RAIL;
  return variant === 'results' && layout === 'stack' ? 'grid gap-4' : 'grid gap-6';
}

/** O card empilhado não comporta a expansão inline (o trilho de 232px
 *  transborda o card): quando a régua vira `stack` — a janela encolheu, a
 *  coluna da viagem abriu — a escada que estava aberta RECOLHE (validação de
 *  30/09). Só a transição: `fares` é lido pela callback estável, fora das deps,
 *  senão cada render recolheria o que a pessoa acabou de abrir. */
function useCollapseOnStack(layout: OfferLayout, fares: Pick<OfferFares, 'collapse'>): void {
  const collapse = useStableCallback(() => {
    fares.collapse();
  });
  useEffect(() => {
    if (layout === 'stack') collapse();
  }, [layout, collapse]);
}

/**
 * A oferta cuja tela cheia de tarifas está aberta (#3875) — por id, e não pelo
 * objeto: a lista re-renderiza com objetos novos e a tela tem de seguir a
 * oferta, não a instância. Fora da lista ⇒ a tela some (nada a mostrar). Só no
 * card empilhado com escada: no desenho largo `control` é `undefined` e o
 * `rowActions` segue com a expansão inline.
 */
function useListFaresDialog<O extends FlightOfferSource, V extends FlightTariffSource>(
  offers: readonly O[],
  fares: OfferFares<O, V>,
  phone: boolean,
) {
  const [openId, setOpenId] = useState<string | null>(null);
  const active = phone && fares.enabled;
  const at = offers.findIndex((o) => o.offerId === openId);
  return {
    offer: active ? offers[at] : undefined,
    position: at + 1,
    close: () => {
      setOpenId(null);
    },
    control: active
      ? {
          openId,
          open: (offer: O) => {
            setOpenId(offer.offerId);
            // O mesmo fetch do toggle: oferta já `ready` não pede de novo.
            fares.fetch(offer);
          },
        }
      : undefined,
  };
}

/** A tela cheia de UMA oferta da lista, lendo a escada do `useOfferFares` como
 *  o `rowActions` lê a expansão inline — e fechando no pick. */
function ListFaresDialog<O extends FlightOfferSource, V extends FlightTariffSource>({
  offer,
  position,
  fares,
  readOnly,
  fareCta,
  env,
  onSelectFares,
  onResearch,
  onClose,
}: {
  readonly offer: O;
  readonly position: number;
  readonly fares: OfferFares<O, V>;
  readonly readOnly: boolean | undefined;
  readonly fareCta: { mode: 'confirm'; disabled?: boolean } | undefined;
  readonly env: OfferListEnv;
  readonly onSelectFares: SelectFaresFn<O, V>;
  readonly onResearch: (() => void) | undefined;
  readonly onClose: () => void;
}) {
  const id = offer.offerId;
  return (
    <OfferFaresDialog
      offer={offer}
      state={fares.stateOf(id)}
      sel={fares.selOf(id)}
      readOnly={readOnly}
      // A mesma régua do rodapé da expansão inline: só o explorer manda
      // `fareCta` (o pick fecha a viagem ⇒ "Ir para checkout"). Nos cards do
      // chat o pick vai ao agente e a conversa segue, então o pé diz "Próximo"
      // — "Selecionar" já é o verbo de cada cartão de tarifa logo acima.
      cta={
        fareCta
          ? { label: env.text.copy.checkout, disabled: Boolean(fareCta.disabled) }
          : { label: env.text.copy.next, disabled: false }
      }
      text={env.text}
      airportSwitchOf={env.airportSwitchOf}
      renderDetail={env.renderDetail}
      onPick={(patch) => {
        fares.pick(id, patch);
      }}
      onSelect={(picked) => {
        onSelectFares(offer, position, fares.stateOf(id), picked, fares.selOf(id));
        onClose();
      }}
      onRetry={() => {
        fares.fetch(offer);
      }}
      // A re-busca troca a lista inteira (no chat, num turno novo da conversa,
      // #5277): a tela cheia da oferta vencida fecha no gesto, como no pick.
      onResearch={
        onResearch &&
        (() => {
          onResearch();
          onClose();
        })
      }
      onClose={onClose}
    />
  );
}
